import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { getPackById } from "../db";
import { putPack } from "./adminApi";
import type { Pack } from "../domain/types";
import { MODES, type ModeId } from "../domain/enums";
import { Button } from "../components/Button";

interface FormState {
  modeId: ModeId;
  title_uk: string;
  title_ru: string;
  title_en: string;
  desc_uk: string;
  desc_ru: string;
  desc_en: string;
  isAdult: boolean;
  sortPriority: number | "";
}

interface Props {
  /** "new" creates a fresh pack; "edit" loads from IDB by :packId. */
  mode: "new" | "edit";
}

function defaultForm(): FormState {
  return {
    modeId: "friends",
    title_uk: "",
    title_ru: "",
    title_en: "",
    desc_uk: "",
    desc_ru: "",
    desc_en: "",
    isAdult: false,
    sortPriority: 50,
  };
}

export default function AdminPackForm({ mode }: Props) {
  const nav = useNavigate();
  const { packId } = useParams<{ packId: string }>();
  const [form, setForm] = useState<FormState>(defaultForm);
  // Existing pack metadata is captured here so Save can preserve fields the
  // form doesn't expose (totalCards, version, status, progressPct, etc.).
  const [existing, setExisting] = useState<Pack | null>(null);

  useEffect(() => {
    if (mode !== "edit" || !packId) return;
    (async () => {
      const p = (await getPackById(packId)) ?? null;
      if (!p) return;
      setExisting(p);
      setForm({
        modeId: p.modeId,
        title_uk: p.titleByLang.uk,
        title_ru: p.titleByLang.ru,
        title_en: p.titleByLang.en,
        desc_uk: p.descByLang.uk,
        desc_ru: p.descByLang.ru,
        desc_en: p.descByLang.en,
        isAdult: p.isAdult,
        sortPriority: p.sortPriority,
      });
    })();
  }, [mode, packId]);

  async function save() {
    if (!form.title_uk.trim()) {
      alert("Назва українською — обовʼязкова");
      return;
    }
    const now = Date.now();
    const sortPriority =
      form.sortPriority === "" ? 50 : Math.max(0, Math.min(999, Number(form.sortPriority)));
    if (mode === "edit" && existing) {
      const updated: Pack = {
        ...existing,
        modeId: form.modeId,
        titleByLang: {
          uk: form.title_uk.trim(),
          ru: form.title_ru.trim(),
          en: form.title_en.trim(),
        },
        descByLang: {
          uk: form.desc_uk.trim(),
          ru: form.desc_ru.trim(),
          en: form.desc_en.trim(),
        },
        isAdult: form.isAdult,
        sortPriority,
      };
      await putPack(updated);
      nav(`/admin/pack/${updated.id}`);
      return;
    }
    // "new" — invent an id under user_ namespace so we never collide with
    // seeded ids (alco-2, couples-1, …) on Reset Content.
    const id = `user_${form.modeId}_${now.toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const pack: Pack = {
      id,
      modeId: form.modeId,
      titleByLang: {
        uk: form.title_uk.trim(),
        ru: form.title_ru.trim(),
        en: form.title_en.trim(),
      },
      descByLang: {
        uk: form.desc_uk.trim(),
        ru: form.desc_ru.trim(),
        en: form.desc_en.trim(),
      },
      isAdult: form.isAdult,
      availableLanguages: ["uk", "ru", "en"],
      publishedAt: now,
      sortPriority,
      version: 1,
      totalCards: 0,
      status: "not_started",
      progressPct: 0,
      userCreated: true,
    };
    await putPack(pack);
    nav(`/admin/pack/${pack.id}`);
  }

  return (
    <div className="flex flex-col gap-3 pb-6">
      <div className="flex items-center gap-2">
        <Button size="sm" variant="ghost" onClick={() => nav("/admin")}>
          ←
        </Button>
        <h3 className="font-display text-lg font-bold flex-1">
          {mode === "new" ? "Нова колода" : "Редагування колоди"}
        </h3>
      </div>

      <Field label="Режим (mode)">
        <select
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.modeId}
          onChange={(e) => setForm({ ...form, modeId: e.target.value as ModeId })}
          disabled={mode === "edit"}
        >
          {MODES.map((m) => (
            <option key={m.id} value={m.id}>
              {m.id}
              {m.isAdult ? " (18+)" : ""}
            </option>
          ))}
        </select>
        {mode === "edit" && (
          <span className="text-[11px] opacity-50 mt-1 block">
            Режим змінити не можна — карток у колоді він би розкидав.
          </span>
        )}
      </Field>

      <Field label="Назва українською (обовʼязково)">
        <input
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.title_uk}
          onChange={(e) => setForm({ ...form, title_uk: e.target.value })}
          placeholder="Наприклад: Гаряча компанія"
        />
      </Field>
      <Field label="Назва російською (необовʼязково)">
        <input
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.title_ru}
          onChange={(e) => setForm({ ...form, title_ru: e.target.value })}
        />
      </Field>
      <Field label="Назва англійською (необовʼязково)">
        <input
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.title_en}
          onChange={(e) => setForm({ ...form, title_en: e.target.value })}
        />
      </Field>

      <Field label="Опис українською">
        <textarea
          rows={2}
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.desc_uk}
          onChange={(e) => setForm({ ...form, desc_uk: e.target.value })}
          placeholder="Коротко — про що колода"
        />
      </Field>
      <Field label="Опис російською">
        <textarea
          rows={2}
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.desc_ru}
          onChange={(e) => setForm({ ...form, desc_ru: e.target.value })}
        />
      </Field>
      <Field label="Опис англійською">
        <textarea
          rows={2}
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.desc_en}
          onChange={(e) => setForm({ ...form, desc_en: e.target.value })}
        />
      </Field>

      <Field label="18+ контент (показує підтвердження перед входом)">
        <label className="flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            checked={form.isAdult}
            onChange={(e) => setForm({ ...form, isAdult: e.target.checked })}
            className="w-5 h-5"
          />
          <span className="text-sm opacity-80">
            Дорослий контент — діалог-підтвердження перед першою грою.
          </span>
        </label>
      </Field>

      <Field label="Пріоритет сортування (вище — раніше у списку; 0–999)">
        <input
          type="number"
          min={0}
          max={999}
          step={5}
          className="w-full rounded-lg bg-white/10 border border-white/15 px-3 py-2"
          value={form.sortPriority}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "") return setForm({ ...form, sortPriority: "" });
            const n = Number(v);
            setForm({
              ...form,
              sortPriority: Number.isFinite(n) ? Math.max(0, Math.min(999, n)) : "",
            });
          }}
        />
      </Field>

      <Button variant="primary" fullWidth onClick={save}>
        {mode === "new" ? "Створити колоду" : "Зберегти зміни"}
      </Button>

      {mode === "new" && (
        <p className="text-[11px] opacity-60 text-center">
          Після створення колода зʼявиться в режимі «{form.modeId}» і в адмінці.
          Потім всередині додаси картки (+ Картка / + 🏆 / + 💀).
        </p>
      )}
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-xs opacity-70 mb-1 block">{label}</span>
      {children}
    </label>
  );
}
